import { RandomUtils } from './util/random-utils';
import type { BookingPosition } from './types';

/**
 * Vergibt pro Position eines `BookingRequest`s genau eine stabile `client_id`,
 * die über (Re-)Submits erhalten bleibt. Das Backend dedupliziert darüber
 * (Unique-Index/Idempotency-Key), sodass ein Retry keine Doppelbuchung erzeugt.
 *
 * Zuordnung über die Objekt-Referenz der Position: Positionen dürfen zwischen
 * `validate()`, `submit()` und Retry nicht kopiert werden.
 */
export class BookingClientIds {
  private readonly ids = new Map<BookingPosition, string>();

  /**
   * Stabile Zuordnung für Pfade mit bereits persistiertem Schlüssel: der
   * Rollback nutzt die `entryId` als `client_id` des Counter-Movements.
   *
   * @param position Position, der die ID fest zugeordnet wird
   * @param clientId Persistierter Schlüssel (z.B. `entryId`)
   * @returns Instanz mit der fest zugeordneten client_id
   */
  public static fixedFor(
    position: BookingPosition,
    clientId: string,
  ): BookingClientIds {
    const instance = new BookingClientIds();
    instance.ids.set(position, clientId);
    return instance;
  }

  /**
   * Liefert die `client_id` der Position. Beim ersten Zugriff wird sie
   * erzeugt, jeder weitere Zugriff liefert dieselbe ID.
   *
   * @param position Position, deren client_id benötigt wird
   * @returns Stabile client_id der Position
   */
  public idFor(position: BookingPosition): string {
    const existing = this.ids.get(position);
    if (existing !== undefined) {
      return existing;
    }
    const id = RandomUtils.uuid();
    this.ids.set(position, id);
    return id;
  }
}
